import { useContext } from "react";

import { getIconForProductId } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/ProductCategoryIcon.tsx";
import {
  DataContext,
  DataContextInterface,
} from "@/provider/DataContextProvider";
import { useListAvailableOptions } from "@/services/api";
import { CampaignPeriodCampaignPeriodEnum } from "@/services/model";
import { formatDiscountDisplayName } from "@/utils/basketUtils";
import { ChargeTypeIDs, mobileAbos } from "@/utils/constants";
import {
  getEntry,
  hasBasketEntryWithVoiceOption,
} from "@/utils/dataContextHelpers";
import { transformProducts } from "@/utils/tariffUtils";

import { useGetCampaignEndDate } from "./useGetCampaignEndDate";

export const useGetFlatPricesAndName: (isRecurring?: boolean) => {
  onceTotal: number;
  onceSubtotal: number;
  recurringTotal: number;
  recurringSubtotal: number;
  flatName: string;
  /** The API's raw download-speed string (e.g. "300"), for data-only tariffs. */
  bandwidthLabel?: string;
  minContractDur?: number;
  flatPrice: number;
  voicePrice: number;
  flatPriceRecurring: number;
  voicePriceRecurring: number;
  discountAmount: number;
  /** Basket discount label (e.g. "B2B 10%"), when a due-now discount is shown. */
  discountName?: string;
  discountEndDate?: Date;
} = (isRecurring) => {
  const { dataContext } = useContext(DataContext);

  // TODO: check 'isRecurring' and remove if not needed

  const entry = getEntry(dataContext);
  const option = entry && entry.options && entry.options[0];
  const selectedOption = dataContext.selectedOption;
  const optionProductId = option?.product?.id;
  const hasVoiceOption = hasBasketEntryWithVoiceOption(dataContext);

  const { data: availableOptions } = useListAvailableOptions(
    dataContext.basket?.id ?? -1,
    0,
    undefined,
    {
      query: {
        enabled:
          !!dataContext.basket?.id && !selectedOption && !!optionProductId,
        refetchOnWindowFocus: false,
      },
    },
  );

  const resolvedOption =
    availableOptions && optionProductId
      ? transformProducts(availableOptions).processedProductsById[
          optionProductId
        ]
      : undefined;

  const flatName =
    selectedOption?.baseDisplayValue ||
    resolvedOption?.baseDisplayValue ||
    "Flat Name";
  const bandwidthLabel =
    selectedOption?.bandwidthLabel ?? resolvedOption?.bandwidthLabel;
  const minContractDur =
    selectedOption?.minContractDur ?? resolvedOption?.minContractDur;

  let flatPrice: number;
  let flatPriceRecurring: number;
  let voicePrice = 0;

  // TODO: replace 'flatPriceRecurring' value with option?.recurringCharges once it's fixed by backend
  if (hasVoiceOption) {
    const voiceCharge = option?.oneTimeCharges?.find(
      (charge) => charge.chargeType?.id === ChargeTypeIDs.VoiceOption,
    );
    const flatOneTimeCharge = option?.oneTimeCharges?.find(
      (charge) => charge.chargeType?.id !== ChargeTypeIDs.VoiceOption,
    );
    voicePrice = voiceCharge?.amount || voiceCharge?.unitAmount || 0;
    flatPrice = flatOneTimeCharge?.amount || 0;
    flatPriceRecurring = flatOneTimeCharge?.unitAmount || 0;
  }
  // Otherwise oneTimeCharge[0] is the flat price
  else {
    flatPrice = option?.oneTimeCharges?.[0]?.amount || 0;
    flatPriceRecurring = option?.oneTimeCharges?.[0]?.unitAmount || 0;
  }

  const voiceRecurringCharge = option?.recurringCharges?.find(
    (charge) => charge.chargeType?.id === ChargeTypeIDs.VoiceOption,
  );
  const voicePriceRecurring =
    voiceRecurringCharge?.unitAmount ??
    voiceRecurringCharge?.amount ??
    voicePrice;

  // Get basket totals with proper null checking
  const basketTotal = dataContext.basket?.total;
  const oneTimeAmount = basketTotal?.oneTime?.amount || 0;

  // For precharged products, we want to show the original price in line items
  // The discount will be shown separately, and the total will be 0
  const oneTimeCampaignAmount = basketTotal?.oneTimeCampaign?.amount || 0;
  const oneTimeDiscountFromEntry =
    option?.discounts?.reduce((sum, discount) => {
      if (
        discount.campaignPeriod?.campaignPeriodEnum ===
        CampaignPeriodCampaignPeriodEnum.one_time
      ) {
        return sum + (discount.amount || 0);
      }

      return sum;
    }, 0) || 0;
  const oneTimeDiscountAmount = Math.max(
    oneTimeCampaignAmount,
    oneTimeDiscountFromEntry,
  );
  const recurringAmount = basketTotal?.recurring?.amount || 0;
  const recurringUndiscountedAmount =
    basketTotal?.recurring?.undiscountedAmount ?? recurringAmount;

  // Get the basket entry to determine SIM type
  const basketEntry = getEntry(dataContext);
  const isEsim = basketEntry?.esim ?? false;

  // For eSIMs, oneTime.amount may be gross; subtract one-time discounts to match Datatrans.
  // For physical SIMs:
  // - Partial discounts (e.g. B2B 8%): subtract from oneTime.amount.
  // - Full option-level discounts (e.g. Starterpaket 100%): subtract so Due Now is 0
  //   (SIM already paid / free) and the discount line is shown.
  // - Campaign amount alone equal to oneTime (precharged flat + payable voice, no option
  //   discount entry): do not subtract — oneTime.amount is already what Datatrans charges.
  const shouldSubtractOneTimeDiscount = isEsim
    ? oneTimeDiscountAmount > 0
    : oneTimeDiscountAmount > 0 &&
      (oneTimeDiscountAmount < oneTimeAmount || oneTimeDiscountFromEntry > 0);
  const onceTotal = shouldSubtractOneTimeDiscount
    ? oneTimeAmount - oneTimeDiscountAmount
    : oneTimeAmount;

  // use oneTimeAmount for subtotal as 'undiscountedOneTimeAmount is not used for sim
  const onceSubtotal = oneTimeAmount;
  // Always use the basket's recurring amounts for accurate totals
  const recurringTotal = recurringAmount;
  const recurringSubtotal = recurringUndiscountedAmount;

  // Calculate discount amount based on context
  let discountAmount = 0;
  if (isRecurring) {
    // For recurring context, use the actual recurring discount amount from basket
    discountAmount = basketTotal?.recurring?.amountDiscounts || 0;
  } else {
    // oneTimeCampaign, with fallback to option-level one-time discounts for purchase-intent
    // baskets where total.oneTimeCampaign is not populated consistently.
    discountAmount = shouldSubtractOneTimeDiscount ? oneTimeDiscountAmount : 0;
  }

  // Prefer a named one-time discount (B2B, Starterpaket); fall back to any named entry.
  // Only expose when a due-now discount line is actually shown.
  const rawDiscountName =
    discountAmount > 0
      ? (option?.discounts?.find(
          (discount) =>
            discount.campaignPeriod?.campaignPeriodEnum ===
              CampaignPeriodCampaignPeriodEnum.one_time &&
            discount.discountName,
        )?.discountName ??
        option?.discounts?.find((discount) => discount.discountName)
          ?.discountName)
      : undefined;
  const discountName = formatDiscountDisplayName(rawDiscountName);

  const { campaignEndDate } = useGetCampaignEndDate(dataContext.basket);

  return {
    onceTotal,
    onceSubtotal,
    recurringTotal,
    recurringSubtotal,
    flatName,
    bandwidthLabel,
    minContractDur,
    flatPrice,
    voicePrice,
    flatPriceRecurring,
    voicePriceRecurring,
    discountAmount,
    discountName,
    campaignEndDate,
  };
};

export const useGetIconForDevice = () => {
  const { dataContext } = useContext(DataContext);
  const entry = getEntry(dataContext);
  const productId = entry?.options?.[0]?.product?.id;

  return getIconForProductId(productId);
};

export const useHasVoiceOption = () => {
  const { dataContext } = useContext(DataContext);

  return hasBasketEntryWithVoiceOption(dataContext);
};

export const useBillingPeriod = () => {
  const { dataContext } = useContext(DataContext);

  return dataContext.selectedOption?.minContractDur || 30;
};

export const hasMobileAbo = (dataContext: DataContextInterface) => {
  const entry = getEntry(dataContext);
  const option = entry && entry.options && entry.options[0];

  return mobileAbos.includes(`${option?.product?.id}`);
};

export const useIsMobileAbo = () => {
  const { dataContext } = useContext(DataContext);

  return hasMobileAbo(dataContext);
};
